import {
  Button,
  Dropdown,
  Icon,
  IconButton,
  Toast,
} from '@fattureincloud/fic-design-system'
import {
  faBuilding,
  faPencil,
  faSuitcase,
} from '@fortawesome/pro-light-svg-icons'
import {
  faArrowCircleUp,
  faArrowDown,
  faArrowUp,
  faBinoculars,
  faEdit,
  faEllipsisH,
  faPlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { faSquare } from '@fortawesome/pro-solid-svg-icons'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useState } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'

import { httpRequest } from '../../common/network'
import { errorMessage, locale } from '../../locales/locale'
import { useAppSelector } from '../../redux/hooks'
import { Confirm } from '../common.components'
import { SectionColumnInfo } from '../definitions'
import AddArticleModal from './AddArticleModal'
import AddSectionModal from './AddSectionModal'
import ChangeParentSectionModal from './ChangeParentSectionModal'
import {
  ArticleDrag,
  Column,
  DropTitle,
  Footer,
  SectionActions,
  SectionTitle,
  SectionType,
  Title,
  UserContainer,
} from './dnd.components'
import EditSectionModal from './EditSectionModal'
import UsageModal from './UsageModal'

interface Props {
  section: SectionColumnInfo
  reloadSection: () => void
  changeView: (permalink: string, type: string) => void
  moveUp: () => void
  moveDown: () => void
}
const SubsectionDropContainer: FC<Props> = (props: Props) => {
  const { section } = props
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteSectionConfirmOpen, setDeleteSectionConfirmOpen] =
    useState(false)
  const [selectedArticleId, setSelectedArticleId] = useState<number>(-1)
  const [articleTitleOpen, setArticleTitleOpen] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState(false)
  const [changingParentSection, setChangingParentSection] = useState<{
    open: boolean
    type: 'sezione' | 'articolo' | undefined
  }>({ open: false, type: undefined })
  const [checkUsage, setCheckUsage] = useState<{
    open: boolean
    type: 'sezione' | 'articolo' | undefined
  }>({ open: false, type: undefined })
  const [addingSubSubsection, setAddingSubSubsection] = useState<boolean>(false)

  const realm = useAppSelector((state) => state.realm.value)

  const { id, title, type, vis_accountant, vis_company } = section

  const deleteSection = () => {
    httpRequest({
      method: 'DELETE',
      path: `help/sections/${section.id}`,
      onSuccess: () => {
        Toast.success(locale('Sezione eliminata'))
        props.reloadSection()
      },
      onError: (res) => Toast.error(errorMessage, { content: res.message }),
    })
  }

  const createNewArticle = (title: string) => {
    httpRequest({
      method: 'POST',
      path: 'help/article',
      data: {
        content: '',
        keywords: '',
        parent_section: Number(section.id),
        realm: realm,
        title: title,
        vis_accountant: 0,
        vis_company: 0,
        is_draft: 1,
      },
      onSuccess: (res: { data: { permalink: string } }) => {
        Toast.success(locale('Articolo creato'))
        props.reloadSection()
        props.changeView(res.data.permalink, 'articolo')
      },
      onError: (res) => Toast.error(errorMessage, { content: res.message }),
    })
  }

  const deleteArticle = () => {
    httpRequest({
      method: 'DELETE',
      path: `help/article/${selectedArticleId}`,
      data: {
        realm: realm,
      },
      onSuccess: () => {
        Toast.success(locale('Articolo eliminato'))
        props.reloadSection()
      },
      onError: (res) => {
        Toast.error(errorMessage, { content: res.message })
      },
    })
  }

  const articleVisibilityIcon = (
    vis_accountant: boolean,
    vis_company: boolean,
    is_draft: boolean,
    isMain: boolean
  ) => {
    const size = isMain ? 'lg' : '1x'
    return (
      <>
        {vis_accountant && (
          <Icon
            icon={faSuitcase}
            size={size}
            color={isMain ? 'orange' : 'grey'}
          />
        )}
        {vis_company && (
          <Icon
            icon={faBuilding}
            size={size}
            color={isMain ? 'green' : 'grey'}
          />
        )}
        {is_draft && (
          <Icon
            icon={faPencil}
            size={size}
            color={isMain ? 'yellow' : 'grey'}
          />
        )}
      </>
    )
  }

  const sectionTypeIcon = (type: SectionType) => {
    if (type === SectionType.Subsection)
      return <Icon icon={faSquare} color={'blue'} size={'lg'} />
    if (type === SectionType.SubSubSection)
      return <Icon icon={faSquare} color={'green'} size={'1x'} />
    if (type === SectionType.SubSubSubSection)
      return <Icon icon={faSquare} color={'yellow'} size={'sm'} />
  }

  const sectionTitle = (
    <SectionTitle>
      <DropTitle>
        {sectionTypeIcon(type)}
        <Title type={type}>
          {id} - {title}{' '}
        </Title>
        {articleVisibilityIcon(vis_accountant, vis_company, false, true)}
      </DropTitle>
      <SectionActions>
        <Button
          color={'blue'}
          type={'secondary'}
          text={locale('Nuovo articolo')}
          iconLeft={{ icon: faPlus }}
          onClick={() => setArticleTitleOpen(true)}
        />
        <Dropdown
          renderTrigger={() => (
            <IconButton
              type={'primary'}
              color={'blue'}
              icon={{ icon: faEllipsisH }}
            />
          )}
          renderContent={() => (
            <>
              <Dropdown.Item
                onClick={() => setIsEditing(true)}
                text={locale('Modifica')}
                icon={{ icon: faEdit, color: 'blue' }}
              />
              {section.type !== SectionType.SubSubSubSection && (
                <Dropdown.Item
                  onClick={() => setAddingSubSubsection(true)}
                  text={locale('Sottosezione')}
                  icon={{
                    icon: faPlus,
                    color: 'green',
                  }}
                />
              )}
              {section.type !== SectionType.Main && (
                <Dropdown.Item
                  onClick={() => {
                    props.moveUp()
                  }}
                  text={locale('Sposta in alto')}
                  icon={{
                    icon: faArrowUp,
                    color: 'yellow',
                  }}
                />
              )}
              {section.type !== SectionType.Main && (
                <Dropdown.Item
                  onClick={() => props.moveDown()}
                  text={locale('Sposta in basso')}
                  icon={{
                    icon: faArrowDown,
                    color: 'yellow',
                  }}
                />
              )}
              <Dropdown.Item
                onClick={(e) => {
                  setChangingParentSection({ open: true, type: 'sezione' })
                  setSelectedArticleId(Number(id))
                  e.stopPropagation()
                }}
                text={locale('Cambia parent section')}
                icon={{
                  icon: faArrowCircleUp,
                  color: 'blue',
                }}
              />
              <Dropdown.Item
                onClick={(e) => {
                  setCheckUsage({
                    open: true,
                    type: 'sezione',
                  })
                  setSelectedArticleId(Number(id))
                  e.stopPropagation()
                }}
                text={locale('Verifica utilizzo')}
                icon={{
                  icon: faBinoculars,
                  color: 'grey',
                }}
              />
              <Dropdown.Item
                onClick={() => setDeleteSectionConfirmOpen(true)}
                text={locale('Elimina')}
                icon={{ icon: faTrash, color: 'red' }}
              />
            </>
          )}
        />
      </SectionActions>
    </SectionTitle>
  )

  return (
    <>
      {checkUsage.open && (
        <UsageModal
          isOpen={checkUsage.open}
          onClose={() => setCheckUsage({ open: false, type: undefined })}
          type={checkUsage.type}
          id={selectedArticleId}
          changeView={props.changeView}
        />
      )}
      {changingParentSection.open && (
        <ChangeParentSectionModal
          onClose={() =>
            setChangingParentSection({ open: false, type: undefined })
          }
          onSuccess={props.reloadSection}
          type={changingParentSection.type}
          articleId={selectedArticleId}
          sectionId={Number(section.id)}
        />
      )}
      {deleteConfirmOpen && (
        <Confirm
          title={locale('Eliminare articolo')}
          type={'warning'}
          question={locale("Vuoi eliminare definitivamente") + locale(" l'articolo?")}
          description={
            locale("Procedendo con l'operazione") + ', ' +
            locale('tutte le informazioni relative ') +
            locale("all'articolo ") +
            locale('verranno eliminate definitivamente.')
          }
          actionText={locale('Elimina')}
          cancelText={locale('Annulla')}
          onAction={deleteArticle}
          isOpen={deleteConfirmOpen}
          setIsOpen={setDeleteConfirmOpen}
        />
      )}
      {deleteSectionConfirmOpen && (
        <Confirm
          title={locale('Eliminare sezione')}
          type={'warning'}
          question={locale('Vuoi eliminare definitivamente') + locale(' la sezione?')}
          description={
            locale("Procedendo con l'operazione") + ', ' +
            locale('tutte le informazioni relative ') +
            locale('alla sezione ') +
            locale('verranno eliminate definitivamente.')
          }
          actionText={locale('Elimina')}
          cancelText={locale('Annulla')}
          onAction={deleteSection}
          isOpen={deleteSectionConfirmOpen}
          setIsOpen={setDeleteSectionConfirmOpen}
        />
      )}
      <Column>
        {sectionTitle}
        {isEditing && (
          <EditSectionModal
            onSuccess={() => {
              props.reloadSection()
              setIsEditing(false)
            }}
            onClose={() => setIsEditing(false)}
            section={section}
            realm={realm}
          />
        )}
        {addingSubSubsection && (
          <AddSectionModal
            parentSectionId={Number(section.id)}
            onClose={() => setAddingSubSubsection(false)}
            onSuccess={() => {
              props.reloadSection()
              setAddingSubSubsection(false)
            }}
          />
        )}
        {articleTitleOpen && (
          <AddArticleModal
            onClose={() => setArticleTitleOpen(false)}
            addArticle={(title) => createNewArticle(title)}
          />
        )}
        <Droppable droppableId={String(id)}>
          {({ innerRef, placeholder }, { isDraggingOver }) => (
            <UserContainer ref={innerRef} isDraggingOver={isDraggingOver}>
              {!isEmpty(section.articles) ? (
                section.articles.map(
                  (
                    {
                      id,
                      is_draft,
                      permalink,
                      title,
                      vis_accountant,
                      vis_company,
                    },
                    index
                  ) => (
                    <Draggable key={id} draggableId={String(id)} index={index}>
                      {(
                        {
                          dragHandleProps: eventHandlers,
                          draggableProps,
                          innerRef,
                        },
                        { isDragging }
                      ) => (
                        <ArticleDrag
                          ref={innerRef}
                          {...draggableProps}
                          {...eventHandlers}
                          isDragging={isDragging}
                          onClick={() => {
                            props.changeView(permalink, 'articolo')
                          }}
                        >
                          <DropTitle>
                            <p>
                              {id} - {title}{' '}
                            </p>
                            {articleVisibilityIcon(
                              vis_accountant,
                              vis_company,
                              is_draft,
                              false
                            )}
                          </DropTitle>
                          <SectionActions>
                            <Button
                              text={locale('Elimina')}
                              color={'grey'}
                              type={'secondary'}
                              iconLeft={{ icon: faTrash, color: 'red' }}
                              onClick={(e) => {
                                setDeleteConfirmOpen(true)
                                setSelectedArticleId(Number(id))
                                e.stopPropagation()
                              }}
                            />
                            <Dropdown
                              renderTrigger={() => (
                                <IconButton
                                  type={'primary'}
                                  color={'blue'}
                                  icon={{ icon: faEllipsisH }}
                                />
                              )}
                              renderContent={() => (
                                <>
                                  <Dropdown.Item
                                    onClick={(e) => {
                                      setChangingParentSection({
                                        open: true,
                                        type: 'articolo',
                                      })
                                      setSelectedArticleId(Number(id))
                                      e.stopPropagation()
                                    }}
                                    text={locale('Cambia parent section')}
                                    icon={{
                                      icon: faArrowCircleUp,
                                      color: 'blue',
                                    }}
                                  />
                                  <Dropdown.Item
                                    onClick={(e) => {
                                      setCheckUsage({
                                        open: true,
                                        type: 'articolo',
                                      })
                                      setSelectedArticleId(Number(id))
                                      e.stopPropagation()
                                    }}
                                    text={locale('Verifica utilizzo')}
                                    icon={{
                                      icon: faBinoculars,
                                      color: 'grey',
                                    }}
                                  />
                                </>
                              )}
                            />
                          </SectionActions>
                        </ArticleDrag>
                      )}
                    </Draggable>
                  )
                )
              ) : (
                <p>{locale('No data')}</p>
              )}
              {placeholder}
              <Footer />
            </UserContainer>
          )}
        </Droppable>
      </Column>
    </>
  )
}

export default SubsectionDropContainer
